import { Box, CircularProgress } from '@mui/material';

import type React from 'react';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  DocumentCategories,
  Draft,
  LoadingStates,
  SupportDoc,
} from '../components/TenderDetails/types';
import { useProposalStructure } from '../hooks/useProposalStructure';
import { useTenderActions } from '../hooks/useTenderActions';
import { useTenderCoreData } from '../hooks/useTenderCoreData';
import { useTenderCVs } from '../hooks/useTenderCVs';
import { useTenderDrafts } from '../hooks/useTenderDrafts';
import { useTenderGenerators } from '../hooks/useTenderGenerators';
import { useTenderPartners } from '../hooks/useTenderPartners';
import { useTenderSupportDocs } from '../hooks/useTenderSupportDocs';
import { shareTenderWithWorkspaceAdmins } from '../services/sharingService';
import { updateTender } from '../services/tenderCoreService';
import { PERMISSIONS } from '../shared/constants/permissions';
import type { User } from '../types/auth';
import { logErrorActivity } from '../utils/activityLogger';

import { useAccount } from './AccountContext';
import { useAuth } from './AuthContext';
import { usePricing } from './PricingContext';
import { useSnackbar } from './SnackbarContext';
import { useThemeMode } from './ThemeContext';

export const EMPTY_OBJECT: Record<string, never> = {};
export const EMPTY_ARRAY: never[] = [];

export function useTenderDetailsValue({ tenderId }: { tenderId: string }) {
  const navigate = useNavigate();

  const { isLoading: isAuthLoading } = useAuth();
  const { account, refreshUserDetails, hasPermission } = useAccount();
  const { themeMode: _themeMode } = useThemeMode();
  const { showSnackbar } = useSnackbar();
  const { pricingSettings } = usePricing();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const actionSectionId = searchParams.get('actionSectionId');
  const stepParam = searchParams.get('step');

  const [isArchiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [isPursueDialogOpen, setPursueDialogOpen] = useState(false);
  const [isPursuingTender, setIsPursuingTender] = useState(false);
  const [forceEnablePursue, setForceEnablePursue] = useState(false);

  const [openStep, setOpenStep] = useState<number | null>(() => {
    if (actionSectionId) {
      return 7; // Phase: Review & Edit Proposal
    }
    if (stepParam) {
      const parsedStep = parseInt(stepParam, 10);
      if (!Number.isNaN(parsedStep)) {
        return parsedStep;
      }
    }
    return null;
  });

  useEffect(() => {
    if (actionSectionId) {
      setOpenStep(8);
    } else if (stepParam) {
      const parsedStep = parseInt(stepParam, 10);
      if (!Number.isNaN(parsedStep)) {
        setOpenStep(parsedStep);
      }
    }
  }, [actionSectionId, stepParam]);

  const [isSavingDefault, setIsSavingDefault] = useState(false);
  // Removed local isCompiling state in favor of tender.compileStatus

  const handleNavigateToFinalize = () => {
    setOpenStep(9);
  };

  const handleNavigateToDownload = () => {
    setOpenStep(10);
  };

  const [reviewStepComplete, setReviewStepComplete] = useState(false);

  const {
    tender,
    setTender,
    hasAddedContextFiles,
    setHasAddedContextFiles,
    users,
    contributors,
    sectionsCount,
    reviewedSectionsCount,
  } = useTenderCoreData(
    tenderId,
    account as (User & { clientId?: string; teamId?: string }) | null,
    hasPermission,
    showSnackbar,
    navigate
  );

  const {
    linkPartnerDialog,
    setLinkPartnerDialog,
    handleContactPartner,
    handleLinkPartner,
    confirmLinkPartner,
    isLinking,
  } = useTenderPartners({
    tender,
    account: account as (User & { clientId?: string }) | null,
    showSnackbar,
  });

  const prevCompileStatusRef = useRef<string | undefined>(undefined);

  // Monitor compile status for snackbar
  useEffect(() => {
    const currentStatus = tender?.compileStatus?.status;
    const prevStatus = prevCompileStatusRef.current;

    // Only show snackbar if we were previously processing and now we are complete
    if (
      prevStatus === 'processing' &&
      (currentStatus?.toLowerCase() === 'complete' || currentStatus?.toLowerCase() === 'success')
    ) {
      showSnackbar('DOCX Compilation Complete!', 'success');
    }

    // Always update ref to current status
    prevCompileStatusRef.current = currentStatus;
  }, [tender?.compileStatus?.status, showSnackbar]);

  const hasCheckedAdminAccess = useRef<string | null>(null);

  useEffect(() => {
    if (
      tender?.id &&
      account?.uid &&
      tender.ownerId === account.uid &&
      tender.encryptionMode === 'envelope'
    ) {
      if (hasCheckedAdminAccess.current !== tender.id) {
        hasCheckedAdminAccess.current = tender.id;
        shareTenderWithWorkspaceAdmins(tender.id).catch((err) =>
          logErrorActivity.occurred({
            errorType: 'auto_share_error',
            errorMessage: 'Failed to auto-share with admins (view)',
            userAction: 'view_tender',
            errorCode: (err as Error).name,
          })
        );
      }
    }
  }, [tender?.id, tender?.ownerId, account?.uid, tender?.encryptionMode]);

  const [loadingStates, setLoadingStates] = useState<LoadingStates>({});

  const prevDocsRef = useRef<(SupportDoc | Draft)[]>([]);
  const isInitialMount = useRef(true);

  const {
    handleUpdateTender,
    handleDownload,
    handleSaveAsDefault,
    handleSaveStructureAsDefault,
    handleArchiveTender,
    handleToggleLibraryOverride,
    handleCVDelete,
    handleDownloadCV,
    handleResetTender,
    handleUpdateServiceStatus,
    handleResetForm,
    handleResetAdHocService,
    handleClientLanguageChange,
  } = useTenderActions({
    tender,
    setTender,
    account: account as (User & { clientId?: string; actualDefaultBucketName?: string }) | null,
    showSnackbar,
    setLoadingStates,
    refreshUserDetails,
    navigate,
    setArchiveDialogOpen,
  });

  const {
    drafts,
    handleCompileDraft,
    handleResetCompile,
    handleDownloadCompile,
    handleDeleteDraft,
    handleGeneratePresentation,
    handleGeneratePdf,
    handleDownloadPresentation,
    handleDownloadPdf,
    handleResetPresentation,
    handleResetPdf,
    isGeneratingPresentation,
    isGeneratingPdf,
    presentationError,
    pdfError,
  } = useTenderDrafts({
    tenderId: tender?.id,
    tender,
    account: account as (User & { clientId?: string; actualDefaultBucketName?: string }) | null,
    isAuthLoading,
    showSnackbar,
    setLoadingStates,
    setDocumentCategories: () => undefined, // Handled by useTenderSupportDocs now
    handleDownload,
  });

  const {
    user_docs,
    tenderForms,
    tenderCVs,
    activeLibraryFiles,
    documentCategories,
    processingFiles,
    uploadValidationWarning,
    isDraggingAdhoc,
    isDraggingReview,
    isDraggingForm,
    setTenderCVs,
    setProcessingFiles,
    setDocumentCategories,
    setActiveLibraryFiles,
    handleFileUpload,
    handleDragEventsReview,
    handleDropReview,
    handleDragEventsAdhoc,
    handleDropAdhoc,
    handleDragEventsForm,
    handleDropForm,
    handleDeleteSupportDoc,
    handleDeleteTenderForm,
    handleGenerateAdHocDocument,
    handleDownloadFormInput,
    handleDownloadFilledForm,
    handleCategoryChange,
  } = useTenderSupportDocs({
    tenderId: tender?.id,
    tender,
    account: account as (User & { clientId?: string; actualDefaultBucketName?: string }) | null,
    isAuthLoading,
    isTenderLoaded: !!tender,
    hasPermission,
    hasSections: sectionsCount > 0,
    drafts,
    showSnackbar,
    setLoadingStates,
    setTender,
    refreshUserDetails,
    handleDownload,
  });

  const {
    proposalStructure,
    isUploadingStructure,
    availableTemplates,
    selectedTemplates,
    quickSettings,
    sensors,
    setProposalStructure,
    handleDragEnd,
    handleHeadingUpdate,
    handleFocuserUpdate,
    removeHeading,
    handleStructureFileUpload,
    handleLoadExtractedStructure,
    downloadStructureTemplate,
    handleTemplateChange,
    handleQuickSettingsChange,
  } = useProposalStructure({
    tenderId: tender?.id,
    tender,
    account: account as (User & { clientId?: string }) | null,
    showSnackbar,
  });

  // Step Completion State
  const [stepCompletion, setStepCompletion] = useState<Record<number, boolean>>({});

  const allCVs = useMemo(() => {
    const userCVs = user_docs.filter(
      (doc) => doc.doc_type === 'cv' || doc.doc_type?.toLowerCase().includes('cv')
    );
    // Filter out userCVs that have same originalFilename as one in tenderCVs to avoid duplicates
    const uniqueUserCVs = userCVs.filter(
      (uCv) => !tenderCVs.some((tCv) => tCv.originalFilename === uCv.originalFilename)
    );

    const baseLibraryCVs = activeLibraryFiles
      .filter((file) => {
        // Exclude if the user has explicitly overridden (deleted/hidden) this file in the tender
        if (tender?.libraryDocOverrides && tender.libraryDocOverrides[file.id] === false) {
          return false;
        }
        return (
          file.type === 'cvs' ||
          file.type === 'cv' ||
          file.type === 'library' ||
          (file.name || '').toLowerCase().includes('cv') ||
          (file.name || '').toLowerCase().includes('resume') ||
          (file.originalFilename || '').toLowerCase().includes('cv') ||
          (file.originalFilename || '').toLowerCase().includes('resume')
        );
      })
      .map(
        (file) =>
          ({
            id: file.id,
            filename: file.name,
            originalFilename: file.originalFilename || file.name,
            doc_type: 'cv',
            storageUri: file.storagePath,
            gcsPath: file.storagePath,
            uploadedAt: file.uploadedAt,
            conversionStatus: file.convertedTxtFilename ? 'success' : file.conversionStatus,
            isLibraryItem: true,
          }) as SupportDoc
      );

    const teamMemberCVs = (tender?.teamMembers || EMPTY_ARRAY)
      .filter((m) => Boolean(m.cvFileUrl))
      .map(
        (m) =>
          ({
            id: m.cvFileUrl || '',
            filename: m.cvFileUrl?.split('/').pop() || m.displayName,
            originalFilename: m.displayName || m.cvFileUrl?.split('/').pop(),
            doc_type: 'cv',
            storageUri: m.cvFileUrl || '',
            gcsPath: m.cvFileUrl || '',
            uploadedAt: new Date(),
            conversionStatus: 'success',
            isLibraryItem: true,
          }) as SupportDoc
      );

    // Combine library and team member CVs, removing duplicates by storageUri
    const allLibraryCVsMap = new Map<string, SupportDoc>();
    for (const cv of [...baseLibraryCVs, ...teamMemberCVs]) {
      const uri = cv.storageUri || cv.gcsPath;
      if (uri && !allLibraryCVsMap.has(uri)) {
        allLibraryCVsMap.set(uri, cv);
      }
    }
    const combinedLibraryCVs = Array.from(allLibraryCVsMap.values());

    const libraryFilenames = new Set(combinedLibraryCVs.map((cv) => cv.originalFilename));

    // Flag tenderCVs as library items if they share a filename with an active library file,
    // which prevents them from being deleted directly from the CV generation table
    // (users should toggle them off in the library manager instead).
    const enhancedTenderCVs = tenderCVs.map((cv) => {
      const isLibraryItem = !!(cv.originalFilename && libraryFilenames.has(cv.originalFilename));
      return { ...cv, isLibraryItem };
    });

    // Filter out libraryCVs that are already generated in tenderCVs to avoid duplicates
    const uniqueLibraryCVs = combinedLibraryCVs.filter(
      (lCv) => !enhancedTenderCVs.some((tCv) => tCv.originalFilename === lCv.originalFilename)
    );

    const combined = [...enhancedTenderCVs, ...uniqueUserCVs, ...uniqueLibraryCVs];
    return combined.sort((a, b) => {
      const nameA = a.originalFilename || a.name || a.filename || '';
      const nameB = b.originalFilename || b.name || b.filename || '';
      return nameA.localeCompare(nameB);
    });
  }, [tenderCVs, user_docs, activeLibraryFiles, tender?.teamMembers, tender?.libraryDocOverrides]);

  const {
    cvGenerationStatus,
    cvServiceStartedAt,
    generatedCVs,
    isDraggingCV,
    handleGenerateCVs,
    handleGenerateSingleCV,
    handleCVUpload,
    handleDragEventsCV,
    handleDropCV,
    handleResetCVService,
    handleResetSingleCV,
    handleReportSingleCVError,
  } = useTenderCVs({
    tenderId: tender?.id,
    tender,
    account: account as (User & { clientId?: string; actualDefaultBucketName?: string }) | null,
    allCVs,
    tenderCVs,
    setTenderCVs,
    setLoadingStates,
    setProcessingFiles,
    showSnackbar,
    refreshUserDetails,
  });

  const { handleRegenerate, handleQuickReview } = useTenderGenerators({
    tender,
    account: account as (User & { clientId?: string; actualDefaultBucketName?: string }) | null,
    setTender,
    showSnackbar,
    setLoadingStates,
    drafts,
  });

  useEffect(() => {
    if (!tenderId || !account?.uid) {
      return;
    }

    // Initialize from Tender data first
    const newStepCompletion: Record<number, boolean> = {};
    if (tender?.supportDocsCompleted) {
      newStepCompletion[5] = true;
    }
    if (tender?.quickSettings?.enabled) {
      newStepCompletion[6] = true;
    }
    if (tender?.structureConfirmed) {
      newStepCompletion[7] = true;
    }

    // Merge with localStorage (legacy/local-only steps)
    const key = `tender_steps_${tenderId}_${account.uid}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const localSteps = JSON.parse(saved);
        setStepCompletion({ ...localSteps, ...newStepCompletion });
      } catch (_e) {
        setStepCompletion(newStepCompletion);
      }
    } else {
      setStepCompletion(newStepCompletion);
    }
  }, [
    tenderId,
    account?.uid,
    tender?.supportDocsCompleted,
    tender?.quickSettings?.enabled,
    tender?.structureConfirmed,
  ]);

  const getStepUpdateData = (step: number, isComplete: boolean): Record<string, unknown> | null => {
    if (step === 5) {
      return { supportDocsCompleted: isComplete };
    }
    if (step === 7) {
      return { structureConfirmed: isComplete };
    }
    if (step === 6) {
      const currentSettings = tender?.quickSettings || {
        ...account?.aiSettings,
        enabled: isComplete,
      };
      return { quickSettings: { ...currentSettings, enabled: isComplete } };
    }
    return null;
  };

  const toggleStepCompletion = async (step: number) => {
    const isComplete = !stepCompletion[step];

    // Optimistic Update
    setStepCompletion((prev) => {
      const newState = { ...prev, [step]: isComplete };
      if (tenderId && account?.uid) {
        localStorage.setItem(`tender_steps_${tenderId}_${account.uid}`, JSON.stringify(newState));
      }
      return newState;
    });

    // Persist to Firestore for specific steps
    if (!tenderId || !account?.uid) {
      return;
    }

    const updateData = getStepUpdateData(step, isComplete);
    if (!updateData) {
      return;
    }

    try {
      await updateTender(tenderId, updateData);
    } catch (_error) {
      showSnackbar('Failed to save step status.', 'error');
    }
  };

  useEffect(() => {
    isInitialMount.current = true;

    // When page opens, check if any documents have already completed and clear their loading states
    if (tender) {
      setLoadingStates((prev) => {
        const newStates = { ...prev };
        for (const key of Object.keys(prev)) {
          if (key.includes('regenerate')) {
            const docType = key.split('_').pop();
            let shouldClearLoading = false;

            if (docType === 'alignmentAnalysis') {
              const status = tender.alignmentAnalysis?.status;
              shouldClearLoading =
                status === 'complete' || status === 'error' || status === 'failed';
            } else if (docType === 'response') {
              const status = tender.draftResponse?.status;
              shouldClearLoading =
                status === 'complete' || status === 'error' || status === 'failed';
            } else if (docType === 'review') {
              const status = tender.reviewResponse?.status;
              shouldClearLoading =
                status === 'complete' || status === 'error' || status === 'failed';
            }

            if (shouldClearLoading) {
              newStates[key] = false;
            }
          }
        }
        return newStates;
      });
    }
  }, [tender]);

  useEffect(() => {
    if (!account?.clientId || !account?.uid || !tender?.id) {
      return;
    }

    // Sync userDocsCount to tender if different
    // Treat undefined as 0 for comparison
    const currentCount = tender.userDocsCount || 0;
    if (currentCount !== user_docs.length) {
      const handler = setTimeout(async () => {
        try {
          if (!tender?.id) {
            return;
          }
          await updateTender(tender.id, { userDocsCount: user_docs.length });
        } catch (_e) {
          // Silent fail or toast
        }
      }, 2000); // Debounce sync

      return () => clearTimeout(handler);
    }
    return undefined;
  }, [user_docs.length, tender?.userDocsCount, account?.clientId, account?.uid, tender?.id]);

  useEffect(() => {
    const currentDocs = [...user_docs, ...drafts];
    const prevDocs = prevDocsRef.current;

    for (const doc of currentDocs) {
      const prevDoc = prevDocs.find(
        (d) => (d.originalFilename || d.filename) === (doc.originalFilename || doc.filename)
      );

      if (
        prevDoc &&
        prevDoc.conversionStatus === 'processing' &&
        (doc.conversionStatus === 'success' || doc.conversionStatus === 'failed')
      ) {
        const docId = doc.originalFilename || doc.filename || '';
        if (doc.conversionStatus === 'success') {
          showSnackbar(`${docId} converted successfully.`, 'success');
        } else {
          showSnackbar(`Conversion failed for ${docId}. Please contact support.`, 'error');
        }
      }
    }

    prevDocsRef.current = currentDocs;
  }, [user_docs, drafts, showSnackbar]);

  useEffect(() => {
    const initialCategories: DocumentCategories = {};
    for (const doc of [...user_docs, ...drafts, ...tenderForms]) {
      const docIdentifier =
        doc.originalFilename?.replace(/\.[^/.]+$/, '') ||
        (doc.filename || doc.name || '').replace(/\.[^/.]+$/, '');

      if (docIdentifier && doc.doc_type) {
        initialCategories[docIdentifier] = doc.doc_type;
      }
    }
    setDocumentCategories(initialCategories);
  }, [user_docs, drafts, tenderForms, setDocumentCategories]);

  if (isAuthLoading || !tender || !tender.id) {
    return null;
  }

  // After the guard, tender.id is guaranteed to be defined
  const validTenderId = tender.id;

  const isOwner = tender.ownerId === account?.uid;
  const isSameClient = tender.clientId === account?.clientId;

  const isGlobalManager =
    isSameClient &&
    hasPermission(PERMISSIONS.TENDER_VIEW_CLIENT) &&
    hasPermission(PERMISSIONS.TENDER_EDIT);

  const isTeamManager =
    isSameClient &&
    hasPermission(PERMISSIONS.TENDER_MANAGE_TEAM) &&
    hasPermission(PERMISSIONS.TENDER_EDIT) &&
    tender.teamId === account?.teamId;

  const userTenderPerms = tender.memberPermissions?.[account?.uid || ''];
  const hasFullAccess =
    isOwner ||
    (Array.isArray(userTenderPerms) && userTenderPerms.includes(PERMISSIONS.TENDER_FULL_ACCESS)) ||
    (typeof userTenderPerms === 'object' &&
      userTenderPerms !== null &&
      !Array.isArray(userTenderPerms) &&
      !!(userTenderPerms as Record<string, boolean>)[PERMISSIONS.TENDER_FULL_ACCESS]);

  const canManageTender = hasFullAccess || isGlobalManager || isTeamManager;
  const isPartnerView =
    account?.clientId &&
    tender?.clientId &&
    account.clientId !== tender.clientId &&
    tender.sharedWith?.includes(account.uid || '');

  // Delete handler for ad-hoc docs

  // Delete handler for tender forms

  // CV Handlers

  const contextValue = {
    tender,
    setTender,
    tenderId,
    validTenderId,
    isOwner,
    isSameClient,
    isGlobalManager,
    isTeamManager,
    hasFullAccess,
    canManageTender,
    isPartnerView,
    account,
    hasPermission,
    users,
    contributors,
    loadingStates,
    setLoadingStates,
    sectionsCount,
    reviewedSectionsCount,
    isArchiveDialogOpen,
    setArchiveDialogOpen,
    isPursueDialogOpen,
    setPursueDialogOpen,
    isPursuingTender,
    setIsPursuingTender,
    forceEnablePursue,
    setForceEnablePursue,
    openStep,
    setOpenStep,
    reviewStepComplete,
    setReviewStepComplete,
    hasAddedContextFiles,
    setHasAddedContextFiles,
    stepCompletion,
    toggleStepCompletion,
    isSavingDefault,
    setIsSavingDefault,

    // Partner Hook
    linkPartnerDialog,
    setLinkPartnerDialog,
    handleContactPartner,
    handleLinkPartner,
    confirmLinkPartner,
    isLinking,

    // Actions Hook
    handleUpdateTender,
    handleDownload,
    handleSaveAsDefault,
    handleSaveStructureAsDefault,
    handleArchiveTender,
    handleToggleLibraryOverride,
    handleCVDelete,
    handleDownloadCV,
    handleResetTender,
    handleUpdateServiceStatus,
    handleResetForm,
    handleResetAdHocService,
    handleClientLanguageChange,

    // Drafts Hook
    drafts,
    handleCompileDraft,
    handleResetCompile,
    handleDownloadCompile,
    handleDeleteDraft,
    handleGeneratePresentation,
    handleGeneratePdf,
    handleDownloadPresentation,
    handleDownloadPdf,
    handleResetPresentation,
    handleResetPdf,
    isGeneratingPresentation,
    isGeneratingPdf,
    presentationError,
    pdfError,

    // Support Docs Hook
    user_docs,
    tenderForms,
    tenderCVs,
    activeLibraryFiles,
    documentCategories,
    processingFiles,
    uploadValidationWarning,
    isDraggingAdhoc,
    isDraggingReview,
    isDraggingForm,
    setTenderCVs,
    setProcessingFiles,
    setDocumentCategories,
    setActiveLibraryFiles,
    handleFileUpload,
    handleDragEventsReview,
    handleDropReview,
    handleDragEventsAdhoc,
    handleDropAdhoc,
    handleDragEventsForm,
    handleDropForm,
    handleDeleteSupportDoc,
    handleDeleteTenderForm,
    handleGenerateAdHocDocument,
    handleDownloadFormInput,
    handleDownloadFilledForm,
    handleCategoryChange,

    // Proposal Structure Hook
    proposalStructure,
    isUploadingStructure,
    availableTemplates,
    selectedTemplates,
    quickSettings,
    sensors,
    setProposalStructure,
    handleDragEnd,
    handleHeadingUpdate,
    handleFocuserUpdate,
    removeHeading,
    handleStructureFileUpload,
    handleLoadExtractedStructure,
    downloadStructureTemplate,
    handleTemplateChange,
    handleQuickSettingsChange,

    // CV Generation Hook
    cvGenerationStatus,
    cvServiceStartedAt,
    generatedCVs,
    isDraggingCV,
    handleGenerateCVs,
    handleGenerateSingleCV,
    handleCVUpload,
    handleDragEventsCV,
    handleDropCV,
    handleResetCVService,
    handleResetSingleCV,
    handleReportSingleCVError,
    allCVs,

    // Generators Hook
    handleRegenerate,
    handleQuickReview,

    // Other
    handleNavigateToFinalize,
    handleNavigateToDownload,
    pricingSettings,
  };

  return contextValue;
}

export type TenderDetailsContextType = NonNullable<ReturnType<typeof useTenderDetailsValue>>;

const TenderDetailsContext = createContext<TenderDetailsContextType | undefined>(undefined);

export const useTenderDetails = () => {
  const context = useContext(TenderDetailsContext);
  if (context === undefined) {
    throw new Error('useTenderDetails must be used within a TenderDetailsProvider');
  }
  return context;
};

export const TenderDetailsProvider: React.FC<{ tenderId: string; children: React.ReactNode }> = ({
  tenderId,
  children,
}) => {
  const contextValue = useTenderDetailsValue({ tenderId });

  if (!contextValue) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TenderDetailsContext.Provider value={contextValue}>{children}</TenderDetailsContext.Provider>
  );
};
